import { useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { Section, SectionHeading } from '../ui/Section'
import { SPRING } from '../../utils/motion'
import { CASE_STUDIES } from '../../data/caseStudies'
import { api } from '../../lib/api'
import { useApi } from '../../lib/useApi'
import { COMPANY } from '../../data/company'

const PER_PAGE = 6

// Fallback used until the API responds (or if it's unavailable).
const SEED = [
  { id: 'nsalu', name: 'Nsalu', url: 'https://www.nsalu.com/', logo_url: '/brand/nsalu.png' },
  { id: 'echara', name: 'Echara', url: 'https://echara.cc/', logo_url: '/brand/echara.png' },
  { id: 'scotch', name: 'Scotch the Wheel', url: 'https://scotchthewheel.com/', logo_url: '/brand/scotchthewheel.jpg' },
  { id: 'wedu', name: 'Wedu', url: 'https://wedu.co.zw/', logo_url: '/brand/wedu.svg' },
  { id: 'connect', name: 'Connect Outsourcing', url: 'https://www.connectoutsourcing.co.zw/', logo_url: '/brand/connect-outsourcing.png' },
  { id: 'tapandgo', name: 'Tap & Go', url: 'https://tapandgo.co.zw', logo_url: '/brand/tapandgo.png' },
  { id: 'gradez', name: 'Gradez', url: '', logo_url: '/brand/gradez.png' },
]

const isLink = (url) => /^https?:\/\//i.test(url || '')
const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

// The companies from the CMS are the list; a case study with the same site
// fills in whatever the CMS doesn't have (industry, year, a description).
function toProjects(clients) {
  const studies = new Map(CASE_STUDIES.map((s) => [hostOf(s.link), s]))
  return clients.map((c) => {
    const study = studies.get(hostOf(c.url))
    return {
      key: c.id ?? c.name,
      name: c.name,
      url: isLink(c.url) ? c.url : '',
      logo: c.logo_url,
      description: c.description || study?.description || '',
      industry: study?.industry,
      year: study?.year,
      live: study?.status === 'live',
    }
  })
}

/* ── What a logo sits on ─────────────────────────────────────────────────
   Logos arrive as transparent marks, on a white plate, or on a dark plate
   (e.g. gold on black). The card's logo well takes on the plate's colour so
   every mark sits naturally in both themes. The border pixels are sampled once
   the image loads; a transparent mark is judged by its art (white art → dark
   well). A cross-origin image can't be read and keeps a light well. */
function plateOf(img) {
  try {
    const n = 24
    const c = document.createElement('canvas')
    c.width = n
    c.height = n
    const ctx = c.getContext('2d', { willReadFrequently: true })
    ctx.drawImage(img, 0, 0, n, n)
    const d = ctx.getImageData(0, 0, n, n).data
    let lum = 0, count = 0, clear = 0
    for (let i = 0; i < n; i++) {
      for (const p of [i, (n - 1) * n + i, i * n, i * n + n - 1]) {
        const o = p * 4
        if (d[o + 3] < 200) clear++
        lum += (0.2126 * d[o] + 0.7152 * d[o + 1] + 0.0722 * d[o + 2]) / 255
        count++
      }
    }
    if (clear / count <= 0.25) return lum / count < 0.4 ? 'dark' : 'light'
    // A transparent mark: judge by the art itself, so white logos get a dark well.
    let art = 0, ink = 0
    for (let o = 0; o < d.length; o += 4) {
      if (d[o + 3] < 128) continue
      art += (0.2126 * d[o] + 0.7152 * d[o + 1] + 0.0722 * d[o + 2]) / 255
      ink++
    }
    return ink && art / ink > 0.8 ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

function ProjectCard({ project, index, reduce }) {
  const [plate, setPlate] = useState('light')
  const [failed, setFailed] = useState(!project.logo)
  const Tag = project.url ? motion.a : motion.div
  const host = hostOf(project.url)

  return (
    <Tag
      {...(project.url
        ? { href: project.url, target: '_blank', rel: 'noopener noreferrer', 'aria-label': `${project.name} — visit ${host} (opens in a new tab)` }
        : {})}
      whileTap={reduce || !project.url ? undefined : { scale: 0.98 }}
      transition={SPRING.snappy}
      className="work-card surface surface-hover group flex h-full flex-col overflow-hidden rounded-3xl outline-none"
    >
      {/* Logo, kept small in a rounded well that takes on the logo's own
          plate colour, with the index and live status alongside. */}
      <div className="flex items-start justify-between gap-3 px-6 pt-6">
        <div
          className={`grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl p-2.5 shadow-[var(--elev-1)] ring-1 md:h-[4.5rem] md:w-[4.5rem] ${
            plate === 'dark' ? 'bg-[#0d0d0f] ring-white/10' : 'bg-white ring-black/[0.06]'
          }`}
        >
          {failed ? (
            <span className={`text-xl font-bold ${plate === 'dark' ? 'text-white' : 'text-neutral-900'}`}>
              {project.name.slice(0, 1)}
            </span>
          ) : (
            <img
              src={project.logo}
              alt=""
              loading="lazy"
              decoding="async"
              draggable="false"
              onLoad={(e) => setPlate(plateOf(e.currentTarget))}
              onError={() => setFailed(true)}
              className="max-h-full max-w-full object-contain"
            />
          )}
        </div>
        <div className="flex items-center gap-2 pt-1">
          {project.live && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-tertiary-container px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-wide text-tertiary-on-container">
              <span className="animate-live h-1.5 w-1.5 rounded-full bg-tertiary" /> live
            </span>
          )}
          <span className="font-mono text-xs font-bold text-ink-3">{String(index + 1).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col px-6 pb-5 pt-5">
        {(project.industry || project.year) && (
          <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-primary">
            {[project.industry, project.year].filter(Boolean).join(' · ')}
          </p>
        )}
        <h3 className="mt-1.5 text-xl font-bold text-ink transition-colors duration-200 group-hover:text-primary">
          {project.name}
        </h3>
        {project.description && (
          <p className="mt-2 line-clamp-3 text-[15px] leading-relaxed text-ink-3">{project.description}</p>
        )}
        <span className="block h-5" aria-hidden="true" />

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-outline-variant pt-4">
          <span className="truncate font-mono text-[13px] font-semibold text-ink-2">{host || 'Private project'}</span>
          {project.url && (
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sc-high text-ink-2 transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-on">
              <ArrowUpRight className="h-[18px] w-[18px]" />
            </span>
          )}
        </div>
      </div>
    </Tag>
  )
}

const pageBtn =
  'surface grid h-11 w-11 place-items-center rounded-full text-ink transition-colors duration-200 ' +
  'hover:bg-sc-high disabled:pointer-events-none disabled:opacity-35 ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40'

/* Selected work and the companies we've built for, as one paginated grid.
   Every card links out to the live product. */
export default function CaseStudies() {
  const clients = useApi(api.clients, SEED)
  const projects = useMemo(() => toProjects(clients), [clients])
  const reduce = useReducedMotion()
  const gridRef = useRef(null)
  const [page, setPage] = useState(0)
  const [dir, setDir] = useState(1)

  const pages = Math.max(1, Math.ceil(projects.length / PER_PAGE))
  const current = Math.min(page, pages - 1)
  const shown = projects.slice(current * PER_PAGE, (current + 1) * PER_PAGE)

  const go = (next) => {
    if (next < 0 || next >= pages || next === current) return
    setDir(next > current ? 1 : -1)
    setPage(next)
    // Bring the grid's top back into view if the reader has scrolled past it.
    const top = gridRef.current?.getBoundingClientRect().top ?? 0
    if (top < 0) gridRef.current.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  }

  return (
    <Section id="portfolio" tone="raised" atmosphere="right">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <SectionHeading
          eyebrow="Selected work"
          title={[
            'Software our clients',
            // Highlighted words are passed one by one so the line can wrap.
            ...'run their business on.'.split(' ').map((w) => <span key={w} className="text-primary">{w}</span>),
          ]}
          lead="HR and payroll platforms, outsourcing portals, education services and consumer apps — each one scoped, engineered and delivered by OrionDev, and in daily use by the people they were built for."
        />
        {/* Two figures, set like the hero's: the client count and the reach. */}
        <dl className="surface flex shrink-0 divide-x divide-outline-variant rounded-2xl md:mb-2">
          {[
            [String(projects.length).padStart(2, '0'), 'clients'],
            [`${COMPANY.stats.projectsDelivered}+`, 'projects'],
            ['175K+', 'end users'],
          ].map(([value, label]) => (
            <div key={label} className="px-5 py-3.5">
              <dd className="font-mono text-2xl font-bold leading-none tracking-tight text-ink">{value}</dd>
              <dt className="mt-1.5 text-xs text-ink-3">{label}</dt>
            </div>
          ))}
        </dl>
      </div>

      <div ref={gridRef} className="mt-14 scroll-mt-28">
        <AnimatePresence mode="wait" initial={false} custom={dir}>
          <motion.ul
            key={current}
            custom={dir}
            initial="enter"
            whileInView="show"
            viewport={{ once: true, margin: '0px 0px -8% 0px' }}
            exit="exit"
            variants={{
              enter: (d) => ({ opacity: 0, x: reduce ? 0 : 16 * d }),
              show: { opacity: 1, x: 0, transition: { duration: 0.35, ease: [0.2, 0, 0, 1], staggerChildren: 0.07 } },
              exit: (d) => ({ opacity: 0, x: reduce ? 0 : -16 * d, transition: { duration: 0.15 } }),
            }}
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:gap-6"
          >
            {shown.map((p, i) => (
              <motion.li
                key={p.key}
                variants={{
                  enter: reduce ? { opacity: 0 } : { opacity: 0, y: 28, rotateX: 10, transformPerspective: 1100 },
                  show: { opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.7, ease: [0.05, 0.7, 0.1, 1] } },
                }}
              >
                <ProjectCard project={p} index={current * PER_PAGE + i} reduce={reduce} />
              </motion.li>
            ))}
          </motion.ul>
        </AnimatePresence>
      </div>

      {pages > 1 && (
        <nav aria-label="Work pages" className="mt-12 flex items-center justify-center gap-3">
          <button type="button" onClick={() => go(current - 1)} disabled={current === 0} aria-label="Previous page" className={pageBtn}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <ol className="surface flex items-center gap-1 rounded-full p-1">
            {Array.from({ length: pages }, (_, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`Page ${i + 1}`}
                  aria-current={i === current ? 'page' : undefined}
                  className={`relative h-9 min-w-[2.5rem] rounded-full px-3 font-mono text-sm font-bold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 ${
                    i === current ? 'text-primary-on' : 'text-ink-3 hover:text-ink'
                  }`}
                >
                  {i === current && (
                    <motion.span layoutId="work-page-pill" transition={SPRING.snappy} className="btn-lit absolute inset-0 rounded-full" />
                  )}
                  <span className="relative">{String(i + 1).padStart(2, '0')}</span>
                </button>
              </li>
            ))}
          </ol>
          <button type="button" onClick={() => go(current + 1)} disabled={current === pages - 1} aria-label="Next page" className={pageBtn}>
            <ChevronRight className="h-5 w-5" />
          </button>
        </nav>
      )}
    </Section>
  )
}
